import {
  lockBodyScroll,
  unlockBodyScroll,
} from '@/globals/typescript/utils/utils'
import { defineStore } from 'pinia'

type ModalPayload = Record<string, unknown>

export type ModalEntry = {
  id: string
  name: string
  props?: ModalPayload
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl'
  useEasyClose?: boolean
  /**
   * Hides ModalHost's own top-right X entirely, for a modal whose only exits
   * are its own explicit buttons (e.g. UnsavedChangesModal's Annuller/Fortsæt)
   * — a plain X next to those reads as a third, unlabelled way out. Escape and
   * useEasyClose are untouched; this only affects the icon's visibility.
   */
  hideCloseButton?: boolean
}

export const useModalStore = defineStore('modal', {
  state: () => ({
    stack: [] as ModalEntry[],
    scrollLockCount: 0,
  }),

  actions: {
    open({
      name,
      props = {},
      width = 'sm',
      useEasyClose = true,
      hideCloseButton = false,
    }: {
      name: string
      props?: ModalPayload
      width?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl'
      useEasyClose?: boolean
      hideCloseButton?: boolean
    }) {
      const id = `${name}:${Date.now()}:${Math.random().toString(16).slice(2)}`
      this.stack.push({ id, name, props, width, useEasyClose, hideCloseButton })

      this.scrollLockCount++
      if (this.scrollLockCount === 1) lockBodyScroll()

      return id
    },

    closeTop() {
      if (!this.stack.length) return
      this.stack.pop()

      this.scrollLockCount = Math.max(0, this.scrollLockCount - 1)
      if (this.scrollLockCount === 0) unlockBodyScroll()
    },

    closeAll() {
      this.stack = []
      this.scrollLockCount = 0
      unlockBodyScroll()
    },
  },

  getters: {
    top: (s) => (s.stack.length ? s.stack[s.stack.length - 1] : null),
  },
})

